import { useState, useEffect } from "react";
import { History, Loader2, Globe, Monitor } from "lucide-react";
import { format, differenceInSeconds } from "date-fns";
import { es } from "date-fns/locale";
import apiClient from "@/lib/api-client";
import Button from "@/components/Button";
import { useGlobalToast } from "../../contexts/ToastContext";

interface PersonRef {
  id: string;
  name: string;
  email: string;
}

interface OrganizationRef {
  id: string;
  name: string;
}

interface ImpersonationLog {
  id: string;
  impersonator: PersonRef | null;
  target: PersonRef | null;
  organization: OrganizationRef | null;
  ipAddress: string | null;
  userAgent: string | null;
  startedAt: string;
  endedAt: string | null;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

function formatDateTime(iso: string): string {
  return format(new Date(iso), "d MMM yyyy, HH:mm", { locale: es });
}

function formatDuration(startedAt: string, endedAt: string): string {
  const seconds = Math.max(
    0,
    differenceInSeconds(new Date(endedAt), new Date(startedAt)),
  );
  if (seconds < 60) return `${seconds} s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const remMinutes = minutes % 60;
  return remMinutes > 0 ? `${hours} h ${remMinutes} min` : `${hours} h`;
}

function PersonCell({ person }: { person: PersonRef | null }) {
  if (!person) {
    return <span className="text-xs text-gray-400 italic">(eliminado)</span>;
  }
  return (
    <div>
      <div className="text-sm font-medium text-gray-900">{person.name}</div>
      <div className="text-xs text-gray-500">{person.email}</div>
    </div>
  );
}

export default function ImpersonationLogs() {
  const { showToast } = useGlobalToast();
  const [logs, setLogs] = useState<ImpersonationLog[]>([]);
  const [pagination, setPagination] = useState<Pagination>({
    page: 1,
    limit: 20,
    total: 0,
    totalPages: 0,
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchLogs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page]);

  const fetchLogs = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        limit: pagination.limit.toString(),
      });
      const response = await apiClient.get(
        `/admin/impersonation-logs?${params}`,
      );
      setLogs(response.data.data);
      setPagination(response.data.pagination);
    } catch (err) {
      console.error(err);
      setError("No se pudo cargar el log de impersonaciones");
      showToast("Error al cargar el log de impersonaciones", "error");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 mb-3">
        <h1 className="text-xl md:text-2xl font-bold text-gray-900 flex items-center gap-2">
          <History className="h-5 w-5 text-primary-600" />
          Log de accesos como cliente
        </h1>
        <p className="text-gray-500 text-sm mt-0.5">
          Historial de sesiones "Acceder como" iniciadas por superadmins
          (solo lectura)
        </p>
      </div>

      {/* Card principal */}
      <div className="flex-1 min-h-0 flex flex-col overflow-hidden bg-white rounded-xl shadow-sm border border-gray-200">
        {isLoading ? (
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
          </div>
        ) : error ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-3 text-center px-4">
            <p className="text-sm text-red-600">{error}</p>
            <Button variant="secondary" onClick={fetchLogs}>
              Reintentar
            </Button>
          </div>
        ) : (
          <div className="flex-1 min-h-0 flex flex-col overflow-hidden">
            {/* Tabla con scroll propio */}
            <div className="flex-1 overflow-y-auto overflow-x-auto">
              <table className="w-full min-w-[1000px]">
                <thead className="bg-gray-50 border-b border-gray-200 sticky top-0 z-10">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Superadmin
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Cliente
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Organización
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      IP
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Inicio
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Fin / Duración
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {logs.length === 0 ? (
                    <tr>
                      <td
                        colSpan={6}
                        className="px-4 py-12 text-center text-gray-500"
                      >
                        No hay registros de impersonación todavía
                      </td>
                    </tr>
                  ) : (
                    logs.map((log) => (
                      <tr key={log.id} className="hover:bg-gray-50">
                        <td className="px-4 py-2.5">
                          <PersonCell person={log.impersonator} />
                        </td>
                        <td className="px-4 py-2.5">
                          <PersonCell person={log.target} />
                        </td>
                        <td className="px-4 py-2.5">
                          {log.organization ? (
                            <span className="text-sm text-gray-900 truncate max-w-[160px] block">
                              {log.organization.name}
                            </span>
                          ) : (
                            <span className="text-xs text-gray-400 italic">
                              (eliminada)
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-2.5">
                          <div className="flex items-center gap-1.5 text-sm text-gray-700">
                            <Globe className="h-3.5 w-3.5 text-gray-400 flex-shrink-0" />
                            {log.ipAddress || "—"}
                          </div>
                          {log.userAgent && (
                            <div
                              className="flex items-center gap-1.5 text-xs text-gray-400 mt-0.5 truncate max-w-[160px]"
                              title={log.userAgent}
                            >
                              <Monitor className="h-3 w-3 flex-shrink-0" />
                              <span className="truncate">
                                {log.userAgent}
                              </span>
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-2.5 text-xs text-gray-500 whitespace-nowrap">
                          {formatDateTime(log.startedAt)}
                        </td>
                        <td className="px-4 py-2.5 text-xs whitespace-nowrap">
                          {log.endedAt ? (
                            <div>
                              <div className="text-gray-500">
                                {formatDateTime(log.endedAt)}
                              </div>
                              <div className="text-gray-400">
                                {formatDuration(log.startedAt, log.endedAt)}
                              </div>
                            </div>
                          ) : (
                            <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-green-100 text-green-800 font-medium">
                              <span className="h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse" />
                              En curso
                            </span>
                          )}
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Paginación fija al fondo */}
            {pagination.totalPages > 1 && (
              <div className="flex-shrink-0 flex items-center justify-between border-t border-gray-200 px-4 py-2.5">
                <div className="text-xs text-gray-500">
                  Mostrando {(pagination.page - 1) * pagination.limit + 1}–
                  {Math.min(
                    pagination.page * pagination.limit,
                    pagination.total,
                  )}{" "}
                  de {pagination.total}
                </div>
                <div className="flex space-x-2">
                  <Button
                    variant="secondary"
                    onClick={() =>
                      setPagination((prev) => ({
                        ...prev,
                        page: prev.page - 1,
                      }))
                    }
                    disabled={pagination.page === 1}
                  >
                    Anterior
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={() =>
                      setPagination((prev) => ({
                        ...prev,
                        page: prev.page + 1,
                      }))
                    }
                    disabled={pagination.page === pagination.totalPages}
                  >
                    Siguiente
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
